import { gql } from '@apollo/client';

// Type definitions for mutations
export interface CreateResponsibilityInput {
  applicationId: number;
  identityId: string;
  identityType: 'USER';
  roleId: number;
}

// Type definition for delete responsibility mutation
export interface DeleteResponsibilityInput {
  responsibilityId: number;
  applicationId: number;
}

// Response types for mutations
export interface ResponsibilityMutationResponse {
  success: boolean;
  message?: string;
}

export interface DeleteResponsibilityMutationResponse {
  success: boolean;
}

export interface CreateResponsibilitiesResponse {
  resources: {
    responsibilities: {
      createResponsibilities: ResponsibilityMutationResponse;
    };
  };
}

export interface DeleteResponsibilityResponse {
  resources: {
    responsibilities: {
      deleteResponsibility: DeleteResponsibilityMutationResponse;
    };
  };
}

/**
 * GraphQL query for fetching functional areas with applications and roles.
 * Used to load functional area data including data leads and applications
 * for the Data Lead Management interface.
 */
export const FunctionalAreasQuery = gql`
  query FunctionalAreasQuery(
    $filter: FunctionalAreaListFilter
    $rolesFilter2: ResourceCatalogRoleFilter
    $pagination: PaginationInput
    $applicationsPagination: PaginationInput
  ) {
    devhub {
      connections {
        functionalAreas(filter: $filter, pagination: $pagination) {
          id
          name
          dataBusinessOwner {
            login
          }
          dataLeads {
            email
            firstName
            lastName
            login
            accountEnabled
          }
          applications(pagination: $applicationsPagination) {
            applicationId
            name
            status
            description
            roles(filter: $rolesFilter2) {
              responsibilities {
                roleId
                responsibilityId
                responsibleParty {
                  ... on User {
                    email
                    firstName
                    lastName
                    login
                    accountEnabled
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * GraphQL query for fetching additional applications for specific functional areas.
 * Used for lazy loading applications when expanding functional area data.
 * @param filter - The filter to apply to the functional areas
 * @param rolesFilter2 - The filter to apply to the roles
 * @param applicationsPagination - The pagination information for the applications
 */
export const AdditionalApplicationsQuery = gql`
  query AdditionalApplicationsQuery(
    $filter: FunctionalAreaListFilter
    $rolesFilter2: ResourceCatalogRoleFilter
    $applicationsPagination: PaginationInput
  ) {
    devhub {
      connections {
        functionalAreas(filter: $filter) {
          id
          name
          applications(pagination: $applicationsPagination) {
            applicationId
            name
            status
            description
            roles(filter: $rolesFilter2) {
              responsibilities {
                roleId
                responsibilityId
                responsibleParty {
                  ... on User {
                    email
                    firstName
                    lastName
                    login
                    accountEnabled
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * GraphQL query for fetching a single application's data lead roles.
 * Used to refresh specific applications after mutations instead of entire functional areas.
 * @param applicationId - The ID of the application to fetch
 * @param filter - The role filter (typically DataLead role)
 */
export const ApplicationDataLeadsQuery = gql`
  query ApplicationDataLeadsQuery(
    $applicationId: Int!
    $filter: ResourceCatalogRoleFilter
  ) {
    resources {
      application(applicationId: $applicationId) {
        applicationId
        name
        status
        description
        roles(filter: $filter) {
          responsibilities {
            roleId
            responsibilityId
            responsibleParty {
              ... on User {
                email
                firstName
                lastName
                login
                accountEnabled
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * GraphQL mutation for creating multiple responsibilities.
 * Used to create data lead assignments for applications.
 */
export const CreateResponsibilitiesMutation = gql`
  mutation CreateResponsibilities($input: [CreateResponsibilityInput!]!) {
    resources {
      responsibilities {
        createResponsibilities(input: $input) {
          success
          message
        }
      }
    }
  }
`;

/**
 * GraphQL mutation for deleting a single responsibility.
 * Used to remove data lead assignments from applications.
 * Note: This takes a single input, so multiple deletions require multiple mutations.
 */
export const DeleteResponsibilityMutation = gql`
  mutation Mutation($input: DeleteResponsibilityInput!) {
    resources {
      responsibilities {
        deleteResponsibility(input: $input) {
          success
        }
      }
    }
  }
`;
